import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PublicNav, PublicFooter } from "@/components/PublicChrome";
import {
  AlertOctagon, AlertTriangle, Clock, BellRing, CalendarDays, ShieldCheck, Lock,
  Users, Layers, ArrowRight, Check,
} from "lucide-react";

const FEATURES = [
  { icon: BellRing, title: "Reminders before, not after", body: "A calm daily summary lands before anything is due — with a Monday snapshot of the week ahead for owners." },
  { icon: CalendarDays, title: "Everything on one calendar", body: "See every renewal date at a glance, colour-coded by status, with public holidays overlaid so nothing lands on a closed day." },
  { icon: Layers, title: "Your categories, your rules", body: "Track permits, policies, licences, registrations — anything with an expiry date. Set reminder windows per type or per item." },
  { icon: Users, title: "Built for teams", body: "Invite teammates as owners, admins or members. Everyone sees what's coming up; roles keep control where it belongs." },
];

const STATUS = [
  { icon: AlertOctagon, label: "Overdue", cls: "text-[hsl(var(--status-overdue))] bg-[hsl(var(--status-overdue)/0.10)]" },
  { icon: AlertTriangle, label: "Due soon", cls: "text-[hsl(var(--status-due-soon))] bg-[hsl(var(--status-due-soon)/0.12)]" },
  { icon: Clock, label: "Upcoming", cls: "text-[hsl(var(--status-upcoming))] bg-[hsl(var(--status-upcoming)/0.10)]" },
];

const STEPS = [
  { n: "1", title: "Add what expires", body: "Log a permit, policy or licence with its due date — in seconds." },
  { n: "2", title: "We keep watch", body: "BIIPP tracks the date and flags it the moment it enters your reminder window." },
  { n: "3", title: "Renew in time", body: "Your team gets a friendly heads-up by email. Mark it renewed and the cycle starts again." },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PublicNav variant="transparent" />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="auth-wash absolute inset-0" aria-hidden="true" />
        <div className="noise absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border bg-card px-3.5 py-1.5 text-xs font-medium text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--status-ok))]" /> Private by design · one workspace per organization
          </span>
          <h1 className="mt-6 font-heading text-4xl sm:text-6xl font-bold tracking-tight leading-[1.05]">
            Never miss a renewal<br className="hidden sm:block" /> again.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">
            BIIPP tracks every permit, policy, licence and registration your organization depends on —
            and reminds your team before each one is due. Calm, reliable, and quietly out of your way.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/signup" data-testid="landing-hero-signup-button">
              <Button size="lg" className="rounded-xl px-7 text-base h-12">
                Start tracking free <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="rounded-xl px-7 text-base h-12">I already have an account</Button>
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            {["No credit card", "Email reminders included", "Set up in a minute"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5"><Check className="h-4 w-4 text-[hsl(var(--status-ok))]" /> {t}</span>
            ))}
          </div>

          {/* Status preview strip */}
          <div className="mx-auto mt-14 max-w-3xl">
            <div className="rounded-2xl border bg-card p-4 sm:p-5 shadow-sm text-left">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold">This week</span>
                <span className="text-xs text-muted-foreground">Acme Logistics</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {STATUS.map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.label} className={`rounded-xl px-3 py-4 ${s.cls}`}>
                      <Icon className="h-5 w-5" />
                      <p className="mt-2 text-2xl font-semibold tabular-nums">{s.label === "Overdue" ? 1 : s.label === "Due soon" ? 3 : 8}</p>
                      <p className="text-xs font-medium opacity-80">{s.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl">
          <h2 className="font-heading text-3xl font-bold tracking-tight">Everything that expires, in one calm place</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">
            Stop scattering renewal dates across spreadsheets, inboxes and sticky notes. BIIPP gives your
            whole team one trustworthy view — and does the remembering for you.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 gap-6">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="rounded-2xl border bg-card p-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary">
                  <Icon className="h-5 w-5 text-foreground" />
                </div>
                <h3 className="mt-4 text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y bg-secondary/40">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-20">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-center">Three steps. Then it just works.</h2>
          <div className="mt-12 grid sm:grid-cols-3 gap-8">
            {STEPS.map((s) => (
              <div key={s.n} className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground font-heading text-lg font-bold">{s.n}</div>
                <h3 className="mt-4 text-lg font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="rounded-3xl border bg-card p-8 sm:p-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--status-ok)/0.12)] px-3 py-1 text-xs font-semibold text-[hsl(var(--status-ok))]">
              <Lock className="h-3.5 w-3.5" /> Security first
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight">Your data stays strictly yours</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              Every organization is fully isolated — no one outside your team can ever see your records.
              Passwords are strongly hashed, sensitive reference numbers are encrypted at rest, and every
              change is written to an audit trail.
            </p>
            <Link to="/signup" className="inline-block mt-6">
              <Button className="rounded-xl">Create your workspace <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </Link>
          </div>
          <ul className="space-y-3">
            {[
              "Strict organization isolation — no cross-tenant access, ever",
              "Encrypted reference numbers (AES) and hashed passwords",
              "Role-based access: owners, admins and members",
              "Full audit log of logins and record changes",
              "Export your data or delete your workspace anytime",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm">
                <ShieldCheck className="h-5 w-5 shrink-0 text-[hsl(var(--status-ok))]" />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-24">
        <div className="rounded-3xl bg-primary text-primary-foreground p-10 sm:p-16 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Put your renewals on autopilot</h2>
          <p className="mx-auto mt-4 max-w-xl text-primary-foreground/80 leading-relaxed">
            Join organizations who never scramble for a lapsed permit again. It takes a minute to set up.
          </p>
          <Link to="/signup" className="inline-block mt-8">
            <Button size="lg" variant="secondary" className="rounded-xl px-8 h-12 text-base">Get started free</Button>
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
